/**
 * Regeneration Dialog Component
 * 
 * Modal dialog for triggering script regeneration with prompt/model selection.
 * Shows cost estimate and allows job queuing.
 * 
 * Implements Issue #234 regeneration requirements.
 */

import logger from '../../core/logger.js';
import { adminClipsService } from '../../services/admin-clips-service.js';
import { authService } from '../../services/auth-service.js';
import { promptsService } from '../../services/prompts-service.js';
import { modelsService } from '../../services/models-service.js';
import toast from '../../ui/toast.js';

/**
 * Available models (based on content strategy docs)
 * Primary: Gemini 2.5 Flash
 * Reference: docs/setup/issue-235-239-240-updates.md
 * 
 * NOTE: These are fallback values. Should fetch from API in production.
 */
const FALLBACK_MODELS = [
  { id: 'gemini:gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google', cost: 0.0016 },
  { id: 'openai:gpt-4o-mini', name: 'GPT-4o Mini', provider: 'OpenAI', cost: 0.002 },
  { id: 'openai:gpt-4o', name: 'GPT-4o', provider: 'OpenAI', cost: 0.015 }
];

/**
 * Regeneration dialog callbacks
 */
interface RegenerationCallbacks {
  onJobQueued: (jobId: string) => void;
  onCancel: () => void;
}

/**
 * Show regeneration dialog
 */
export async function showRegenerationDialog(
  clipId: string,
  clipLanguage: string,
  clipLevel: string,
  callbacks: RegenerationCallbacks
): Promise<void> {
  logger.info('admin: Showing regeneration dialog', { clipId });
  
  // Fetch prompts and models from API
  let prompts;
  let models;
  
  try {
    // Fetch approved prompts filtered by language and level
    const promptsResponse = await promptsService.listPrompts({
      status: 'approved',
      language: clipLanguage,
      level: clipLevel
    });
    prompts = promptsResponse.prompts || [];
    
    // If no prompts found, show helpful message
    if (prompts.length === 0) {
      logger.warn('admin: No approved prompts found', { clipLanguage, clipLevel });
      toast.error(`No approved prompts available for ${clipLanguage.toUpperCase()} at ${clipLevel} level. Create and approve one in the Prompts tab first.`);
      return;
    }
    
    // Fetch enabled models
    const modelsResponse = await modelsService.listModels({ enabled: true });
    models = modelsResponse.models || [];
    
    // Fallback to hardcoded models if API fails
    if (models.length === 0) {
      logger.warn('admin: No models from API, using fallback');
      models = FALLBACK_MODELS;
    }
  } catch (error) {
    logger.error('admin: Failed to fetch prompts/models', { error });
    toast.error('Failed to load prompts and models');
    return;
  }
  
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'regeneration-modal-overlay';
  
  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-header">
      <h2 class="modal-title">Regenerate Script</h2>
      <button class="modal-close" id="close-regeneration-modal" aria-label="Close">×</button>
    </div>
    
    <div class="modal-body">
      <p class="modal-description">
        Select a prompt and model to regenerate the script for this clip.
        The original content will be preserved until you accept the changes.
      </p>
      
      <div class="form-group">
        <label for="regen-prompt">Prompt Template</label>
        <select id="regen-prompt" class="form-input">
          <option value="">Select a prompt...</option>
          ${prompts.map(p => `<option value="${p.id}">${p.title}</option>`).join('')}
        </select>
        <small class="form-help">Choose a prompt template appropriate for ${clipLanguage.toUpperCase()} at ${clipLevel} level</small>
      </div>
      
      <div class="form-group">
        <label for="regen-model">AI Model</label>
        <select id="regen-model" class="form-input">
          <option value="">Select a model...</option>
          ${models
            .map(m => {
              const cost = m.estimated_cost_per_1k_tokens || 0;
              const displayName = m.id.split(':')[1] || m.id;
              return `<option value="${m.id}">${displayName} (${m.provider}) - ~$${cost.toFixed(3)}</option>`;
            })
            .join('')}
        </select>
        <small class="form-help">Model selection affects quality and cost</small>
      </div>
      
      <div class="cost-estimate-box" id="cost-estimate-box" style="display: none;">
        <div class="cost-estimate-label">Estimated Cost:</div>
        <div class="cost-estimate-value" id="cost-estimate-value">$0.00</div>
        <div class="cost-estimate-note">
          This is an estimate. Actual cost may vary based on content length.
        </div>
      </div>
      
      <div class="form-group">
        <label>
          <input type="checkbox" id="regen-auto-validate" checked>
          Run validation automatically after regeneration
        </label>
      </div>
    </div>
    
    <div class="modal-footer">
      <button class="btn btn--secondary" id="cancel-regeneration">Cancel</button>
      <button class="btn btn--primary" id="submit-regeneration" disabled>
        Queue Regeneration Job
      </button>
    </div>
  `;
  
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // Get form elements
  const promptSelect = document.getElementById('regen-prompt') as HTMLSelectElement;
  const modelSelect = document.getElementById('regen-model') as HTMLSelectElement;
  const costEstimateBox = document.getElementById('cost-estimate-box') as HTMLElement;
  const costEstimateValue = document.getElementById('cost-estimate-value') as HTMLElement;
  const submitBtn = document.getElementById('submit-regeneration') as HTMLButtonElement;
  
  // Update cost estimate when model changes
  const updateCostEstimate = () => {
    const selectedModel = modelSelect.value;
    if (!selectedModel) {
      costEstimateBox.style.display = 'none';
      return;
    }
    
    const model = models.find(m => m.id === selectedModel);
    if (model && model.estimated_cost_per_1k_tokens) {
      // Show cost per 1k tokens
      costEstimateValue.textContent = `$${model.estimated_cost_per_1k_tokens.toFixed(4)}/1k tokens`;
      costEstimateBox.style.display = 'block';
    }
  };
  
  // Enable submit button when both selections are made
  const checkFormValid = () => {
    const isValid = promptSelect.value && modelSelect.value;
    submitBtn.disabled = !isValid;
  };
  
  promptSelect.addEventListener('change', checkFormValid);
  modelSelect.addEventListener('change', () => {
    updateCostEstimate();
    checkFormValid();
  });
  
  // Close handlers
  const closeModal = () => {
    overlay.remove();
    callbacks.onCancel();
  };
  
  document.getElementById('close-regeneration-modal')?.addEventListener('click', closeModal);
  document.getElementById('cancel-regeneration')?.addEventListener('click', closeModal);
  
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeModal();
    }
  });
  
  // Submit handler
  submitBtn.addEventListener('click', async () => {
    const promptId = promptSelect.value;
    const modelId = modelSelect.value;
    const autoValidate = (document.getElementById('regen-auto-validate') as HTMLInputElement)?.checked;
    
    if (!promptId || !modelId) {
      toast.error('Please select both prompt and model');
      return;
    }
    
    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Queueing job...';
      
      const result = await adminClipsService.regenerateClip(
        clipId,
        promptId,
        modelId,
        { autoValidate }
      );
      
      toast.success('Regeneration job queued successfully!');
      overlay.remove();
      
      callbacks.onJobQueued(result.job.id);
      
    } catch (error) {
      logger.error('admin: Failed to queue regeneration', { error });
      toast.error('Failed to queue regeneration job');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Queue Regeneration Job';
    }
  });
}

/**
 * Show job status dialog
 */
export function showJobStatusDialog(jobId: string): void {
  logger.info('admin: Showing job status dialog', { jobId });
  
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'job-status-modal-overlay';
  
  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-header">
      <h2 class="modal-title">Regeneration Job Status</h2>
      <button class="modal-close" id="close-job-status-modal" aria-label="Close">×</button>
    </div>
    
    <div class="modal-body">
      <div class="job-status-container" id="job-status-container">
        <div class="job-status-loading">
          <div class="spinner"></div>
          <p>Loading job status...</p>
        </div>
      </div>
    </div>
    
    <div class="modal-footer">
      <button class="btn btn--secondary" id="close-job-dialog">Close</button>
    </div>
  `;
  
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // Store polling timeout ID for cleanup
  let pollingTimeoutId: number | null = null;
  
  // Close handlers
  const closeModal = () => {
    // Clear any active polling
    if (pollingTimeoutId !== null) {
      clearTimeout(pollingTimeoutId);
      pollingTimeoutId = null;
    }
    overlay.remove();
  };
  
  document.getElementById('close-job-status-modal')?.addEventListener('click', closeModal);
  document.getElementById('close-job-dialog')?.addEventListener('click', closeModal);
  
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeModal();
    }
  });
  
  // Poll for job status with cleanup support
  pollJobStatus(jobId, (timeoutId) => {
    pollingTimeoutId = timeoutId;
  });
}

/**
 * Helper function to escape HTML for safe display
 */
function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Poll job status with exponential backoff
 */
async function pollJobStatus(
  jobId: string, 
  onTimeoutScheduled?: (timeoutId: number) => void,
  pollInterval: number = 2000
): Promise<void> {
  const container = document.getElementById('job-status-container');
  if (!container) return;
  
  try {
    // Fetch job status from API
    const token = authService.getAuthToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch('/api/jobs', {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'get', id: jobId })
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch job status');
    }
    
    const data = await response.json();
    const job = data.job;
    
    // Render job status
    const isTerminal = job.status === 'succeeded' || job.status === 'failed';
    
    container.innerHTML = `
      <div class="job-status-info ${isTerminal ? 'job-status-terminal' : ''}">
        ${isTerminal && job.status === 'succeeded' ? '<div class="job-status-banner job-status-banner--success">✓ Job completed successfully</div>' : ''}
        ${isTerminal && job.status === 'failed' ? '<div class="job-status-banner job-status-banner--error">✗ Job failed</div>' : ''}
        
        <div class="job-status-field">
          <strong>Job ID:</strong> ${job.id}
        </div>
        <div class="job-status-field">
          <strong>Status:</strong> 
          <span class="job-status-badge job-status-badge--${job.status}">${job.status}</span>
        </div>
        <div class="job-status-field">
          <strong>Type:</strong> ${job.type}
        </div>
        <div class="job-status-field">
          <strong>Created:</strong> ${new Date(job.created_at).toLocaleString()}
        </div>
        ${job.started_at ? `
          <div class="job-status-field">
            <strong>Started:</strong> ${new Date(job.started_at).toLocaleString()}
          </div>
        ` : ''}
        ${job.finished_at ? `
          <div class="job-status-field">
            <strong>Finished:</strong> ${new Date(job.finished_at).toLocaleString()}
          </div>
        ` : ''}
        ${job.cost_estimate ? `
          <div class="job-status-field">
            <strong>Estimated Cost:</strong> $${job.cost_estimate.toFixed(3)}
          </div>
        ` : ''}
        ${job.cost_actual ? `
          <div class="job-status-field">
            <strong>Actual Cost:</strong> $${job.cost_actual.toFixed(3)}
          </div>
        ` : ''}
        ${job.error ? `
          <div class="job-status-field job-status-field--error">
            <strong>Error:</strong> 
            <pre class="job-error-message">${escapeHtml(job.error)}</pre>
          </div>
        ` : ''}
        ${job.outputs ? `
          <div class="job-status-field">
            <strong>Outputs:</strong>
            <pre class="job-outputs">${escapeHtml(JSON.stringify(job.outputs, null, 2))}</pre>
          </div>
        ` : ''}
        
        <div class="job-status-actions">
          <button class="btn btn--secondary btn--small" id="copy-job-json">
            📋 Copy Job JSON
          </button>
        </div>
      </div>
    `;
    
    // Add copy button handler
    const copyBtn = document.getElementById('copy-job-json');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(JSON.stringify(job, null, 2))
          .then(() => toast.success('Job JSON copied to clipboard'))
          .catch(() => toast.error('Failed to copy to clipboard'));
      });
    }
    
    // If still running, poll again with exponential backoff
    if (job.status === 'queued' || job.status === 'running') {
      // Implement exponential backoff: 2s, 4s, 8s, 16s, up to max 30s
      const nextInterval = Math.min(pollInterval * 2, 30000);
      
      const timeoutId = window.setTimeout(() => {
        pollJobStatus(jobId, onTimeoutScheduled, nextInterval);
      }, pollInterval);
      
      // Notify caller of scheduled timeout for cleanup
      if (onTimeoutScheduled) {
        onTimeoutScheduled(timeoutId);
      }
    }
    
  } catch (error) {
    logger.error('admin: Failed to fetch job status', { jobId, error });
    container.innerHTML = `
      <div class="error-message">
        Failed to fetch job status. Please try again.
      </div>
    `;
  }
}
